/**
 * Decision 0144 (experiment 11, README §3): a ref's type icon and its one status mark. Icons are monochrome, 12 px, a
 * 1.6 px stroke (the mockup's symbols); colour is only ever used for the status mark, and every mark has its own shape
 * (dot, ring, bar, check, dash), so colour is never the only signal.
 */
import type { RefMark, RefType } from "@minga/protocol"
import { cn } from "@/lib/utils"

const PATHS: Record<string, React.ReactNode> = {
  task: <rect x="2.5" y="2.5" width="11" height="11" rx="2" />,
  bug: (
    <>
      <ellipse cx="8" cy="9" rx="3.5" ry="4.5" />
      <path d="M8 4.5V13.5M4.5 9H2M14 9h-2.5M5 5.5 3 4M11 5.5 13 4M5 12.5 3 14M11 12.5l2 1.5" />
    </>
  ),
  feature: <path d="M8 2l1.8 3.9 4.2.5-3.1 2.9.8 4.2L8 11.4 4.3 13.5l.8-4.2L2 6.4l4.2-.5z" />,
  question: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M6.3 6.4a1.8 1.8 0 1 1 2.5 1.7c-.5.2-.8.6-.8 1.1v.3M8 11.4v.1" />
    </>
  ),
  milestone: <path d="M4 14V2.5M4 3h7.5l-1.5 2.5L11.5 8H4" />,
  card: <path d="M8 2.2 13.8 8 8 13.8 2.2 8z" />,
  run: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M6.8 5.8v4.4L10.3 8z" />
    </>
  ),
  validator: (
    <>
      <path d="M8 2 13 4v4c0 3-2.2 5-5 6-2.8-1-5-3-5-6V4z" />
      <path d="M5.8 8.2 7.4 9.8 10.4 6.6" />
    </>
  ),
  output: (
    <>
      <path d="M4 2h5.5L12 4.5V14H4z" />
      <path d="M9.5 2v2.5H12M6 8h4M6 10.5h4" />
    </>
  ),
  goal: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <circle cx="8" cy="8" r="2.5" />
    </>
  ),
  kpi: <path d="M2.5 13.5h11M3.5 11l3-3.5 2.5 2L13 4.5" />,
  memory: <path d="M4.5 2.5h7v11L8 11l-3.5 2.5z" />,
  decision: (
    <path d="M8 2.5v11M4 13.5h8M3 5.5h10M3 5.5 1.8 9a1.7 1.7 0 0 0 2.4 0zM13 5.5 11.8 9a1.7 1.7 0 0 0 2.4 0z" />
  ),
  open: <path d="M9 3h4v4M13 3 7.5 8.5M11 9.5V13H3V5h3.5" />,
  reply: <path d="M6 4 2.5 7.5 6 11M2.5 7.5H10a3.5 3.5 0 0 1 3.5 3.5v1" />,
  // decision 0148: a person's connected agent (an app that works for them)
  participant: (
    <>
      <rect x="3" y="5.5" width="10" height="7.5" rx="2" />
      <path d="M8 5.5V3M6.2 9.2h.1M9.8 9.2h.1" />
    </>
  ),
}

/** The icon key of a ref: its type, and for an item its variant (a bug, a feature, a question, a plannable item). */
export const iconKeyOf = (type: RefType, variant: string): string => {
  if (type !== "item") return type
  const v = variant.toLowerCase()
  if (v === "bug" || v === "feature" || v === "question") return v
  if (v === "release" || v === "bet" || v === "workstream") return "milestone"
  return "task"
}

/** A 16-unit stroke icon (`name`: a ref icon key, `open` or `reply`). */
export function Glyph({
  name,
  size = 12,
  className,
}: {
  name: string
  size?: number
  className?: string | undefined
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("shrink-0", className)}
    >
      {PATHS[name] ?? PATHS.task}
    </svg>
  )
}

export function RefIcon({
  type,
  variant,
  className,
}: {
  type: RefType
  variant: string
  className?: string | undefined
}) {
  return <Glyph name={iconKeyOf(type, variant)} className={className} />
}

/** The mark's meaning in words (for the chip's name and the legend). */
export const MARK_WORDS: Record<RefMark, string> = {
  needs: "waiting on you",
  live: "running",
  blocked: "blocked",
  wait: "open",
  done: "done",
  gone: "cancelled",
  none: "",
}

/** One status mark: a filled dot (waits on you), a ringed dot (running), a bar, a hollow ring, a check, a dash. */
export function MarkGlyph({ mark, className }: { mark: RefMark; className?: string | undefined }) {
  if (mark === "none") return null
  const base = "inline-block shrink-0"
  switch (mark) {
    case "needs":
      return (
        <span
          aria-hidden="true"
          data-mark="needs"
          className={cn(base, "size-[7px] rounded-full bg-needs", className)}
        />
      )
    case "live":
      return (
        <span
          aria-hidden="true"
          data-mark="live"
          className={cn(
            base,
            "size-[7px] rounded-full bg-live shadow-[0_0_0_2px_var(--color-live-soft)]",
            className,
          )}
        />
      )
    case "blocked":
      return (
        <span
          aria-hidden="true"
          data-mark="blocked"
          className={cn(base, "h-[3px] w-2 rounded-[1px] bg-blocked", className)}
        />
      )
    case "wait":
      return (
        <span
          aria-hidden="true"
          data-mark="wait"
          className={cn(base, "size-[7px] rounded-full border-[1.4px] border-faint", className)}
        />
      )
    case "done":
      return (
        <span
          aria-hidden="true"
          data-mark="done"
          className={cn(base, "text-[12px] leading-none font-semibold text-live", className)}
        >
          ✓
        </span>
      )
    case "gone":
      return (
        <span
          aria-hidden="true"
          data-mark="gone"
          className={cn(base, "text-[12px] leading-none font-semibold text-muted", className)}
        >
          –
        </span>
      )
  }
}
